import React, { useEffect, useMemo, useState } from "react";
import { adminAPI, courseAPI, schoolAPI, walkthroughAPI } from "../services/api";
import ScopeSequenceEditorModal from "../components/ScopeSequenceEditorModal";
import UnitGuideEditorModal from "../components/UnitGuideEditorModal";
import AdminPromotionModal from "../components/AdminPromotionModal";
import "./SettingsPage.css";

const CATEGORY_OPTIONS = [
  "MS Math",
  "MS RLA",
  "MS Science",
  "MS Social Studies",
  "HS Math",
  "HS RLA",
  "HS Science",
  "HS Social Studies",
];
const defaultCategoryForCourse = (course) =>
  Number(course?.grade_level) <= 8 ? "MS Science" : "HS Science";

const sortCourses = (courses) =>
  [...courses].sort((a, b) => {
    const cat = String(a.course_category || "").localeCompare(
      String(b.course_category || ""),
    );
    if (cat !== 0) return cat;
    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const formatDateTime = (value) => {
  if (!value) return "N/A";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "N/A";
  return parsed.toLocaleString();
};

const formatDate = (value) => {
  if (!value) return "Not set";

  const raw = String(value).trim();
  if (!raw) return "Not set";

  const datePortionMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  let parsed;

  if (datePortionMatch) {
    const [, year, month, day] = datePortionMatch;
    parsed = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    parsed = new Date(raw);
  }

  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const SettingsPage = () => {
  const [courses, setCourses] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [sortMode, setSortMode] = useState("category-name");
  const [drafts, setDrafts] = useState({});
  const [selectedRows, setSelectedRows] = useState({});
  const [bulkCategory, setBulkCategory] = useState("HS Science");
  const [savingIds, setSavingIds] = useState({});
  const [isScopeEditorOpen, setIsScopeEditorOpen] = useState(false);
  const [isUnitGuideEditorOpen, setIsUnitGuideEditorOpen] = useState(false);
  const [isAdminPromotionOpen, setIsAdminPromotionOpen] = useState(false);
  const [isPrincipalSectionOpen, setIsPrincipalSectionOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [principalEmail, setPrincipalEmail] = useState("");
  const [principalSchoolIds, setPrincipalSchoolIds] = useState(new Set());
  const [principalPromoteMsg, setPrincipalPromoteMsg] = useState({ type: "", text: "" });
  const [promotingPrincipal, setPromotingPrincipal] = useState(false);
  const [isCurriculumOnlyMode, setIsCurriculumOnlyMode] = useState(false);
  const [syncCourseId, setSyncCourseId] = useState("");
  const [syncSchoolYear, setSyncSchoolYear] = useState("2025-2026");
  const [syncingCalendarWindows, setSyncingCalendarWindows] = useState(false);
  const [previewingCalendarWindows, setPreviewingCalendarWindows] = useState(false);
  const [syncStatus, setSyncStatus] = useState("");
  const [syncError, setSyncError] = useState("");
  const [syncPreviewData, setSyncPreviewData] = useState(null);
  const [isSyncPreviewOpen, setIsSyncPreviewOpen] = useState(false);

  const [newCourseName, setNewCourseName] = useState("");
  const [newCourseGrade, setNewCourseGrade] = useState("12");
  const [newCourseCategory, setNewCourseCategory] = useState(
    "HS Social Studies",
  );

  const loadAdminData = async () => {
    setLoading(true);
    setError("");
    try {
      const coursesRes = await courseAPI.getAll();
      setCourses(sortCourses(coursesRes.data || []));

      try {
        const overviewRes = await adminAPI.getOverview();
        setOverview(overviewRes.data || null);
      } catch (overviewErr) {
        console.warn("Admin overview endpoint unavailable:", overviewErr);
        setOverview(null);
      }

      setDrafts({});
      setSelectedRows({});
    } catch (err) {
      console.error("Error loading admin page:", err);
      setError("Unable to load admin data.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (courses.length === 0) {
      setSyncCourseId("");
      return;
    }
    if (!syncCourseId || !courses.some((course) => course.id === syncCourseId)) {
      setSyncCourseId(courses[0].id);
    }
  }, [courses, syncCourseId]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => window.clearTimeout(handle);
  }, [search]);

  const filteredCourses = useMemo(() => {
    const needle = debouncedSearch.trim().toLowerCase();
    return courses.filter((course) => {
      const shouldFilterBySearch = needle.length >= 1;
      const matchesSearch =
        !shouldFilterBySearch ||
        String(course.name || "").toLowerCase().includes(needle) ||
        String(course.grade_level || "").includes(needle);
      const matchesCategory =
        categoryFilter === "ALL" || course.course_category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [courses, categoryFilter, debouncedSearch]);

  const visibleCourses = useMemo(() => {
    const rows = [...filteredCourses];
    switch (sortMode) {
      case "name-desc":
        return rows.sort((a, b) =>
          String(b.name || "").localeCompare(String(a.name || "")),
        );
      case "grade-asc":
        return rows.sort(
          (a, b) =>
            Number(a.grade_level || 0) - Number(b.grade_level || 0)
            || String(a.name || "").localeCompare(String(b.name || "")),
        );
      case "grade-desc":
        return rows.sort(
          (a, b) =>
            Number(b.grade_level || 0) - Number(a.grade_level || 0)
            || String(a.name || "").localeCompare(String(b.name || "")),
        );
      case "name-asc":
        return rows.sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || "")),
        );
      case "category-name":
      default:
        return sortCourses(rows);
    }
  }, [filteredCourses, sortMode]);

  const dirtyCount = useMemo(() => Object.keys(drafts).length, [drafts]);

  const selectedIds = useMemo(
    () => Object.keys(selectedRows).filter((id) => selectedRows[id]),
    [selectedRows],
  );

  const upsertDraft = (course, patch) => {
    const current = drafts[course.id] || {
      name: course.name,
      grade_level: course.grade_level,
      course_category: course.course_category || defaultCategoryForCourse(course),
    };
    const next = { ...current, ...patch };
    const isUnchanged =
      next.name === course.name &&
      Number(next.grade_level) === Number(course.grade_level) &&
      next.course_category === course.course_category;

    setDrafts((prev) => {
      const updated = { ...prev };
      if (isUnchanged) {
        delete updated[course.id];
      } else {
        updated[course.id] = next;
      }
      return updated;
    });
  };

  const saveCourse = async (courseId) => {
    const draft = drafts[courseId];
    if (!draft) return;

    setSavingIds((prev) => ({ ...prev, [courseId]: true }));
    try {
      const response = await courseAPI.update(courseId, {
        name: draft.name,
        grade_level: Number(draft.grade_level),
        course_category: draft.course_category,
      });
      const updated = response.data;
      setCourses((prev) =>
        sortCourses(prev.map((c) => (c.id === courseId ? updated : c))),
      );
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[courseId];
        return next;
      });
    } catch (err) {
      console.error("Error saving course:", err);
      setError(`Failed to save course changes for ${courseId}.`);
    } finally {
      setSavingIds((prev) => ({ ...prev, [courseId]: false }));
    }
  };

  const saveAllChanges = async () => {
    const ids = Object.keys(drafts);
    for (const id of ids) {
      // Sequential on purpose to keep error handling simple and deterministic.
      // eslint-disable-next-line no-await-in-loop
      await saveCourse(id);
    }
    await loadAdminData();
  };

  const applyBulkCategory = () => {
    if (selectedIds.length === 0) return;
    selectedIds.forEach((id) => {
      const course = courses.find((c) => c.id === id);
      if (!course) return;
      upsertDraft(course, { course_category: bulkCategory });
    });
  };

  const resetCourseDraft = (courseId) => {
    setDrafts((prev) => {
      const next = { ...prev };
      delete next[courseId];
      return next;
    });
  };

  const resetAllDrafts = () => {
    setDrafts({});
  };

  const selectVisibleRows = () => {
    setSelectedRows((prev) => {
      const next = { ...prev };
      visibleCourses.forEach((course) => {
        next[course.id] = true;
      });
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedRows({});
  };

  const createCourse = async (e) => {
    e.preventDefault();
    if (!newCourseName.trim()) return;
    try {
      await courseAPI.create({
        name: newCourseName.trim(),
        grade_level: Number(newCourseGrade),
        course_category: newCourseCategory,
      });
      setNewCourseName("");
      setNewCourseGrade("12");
      setNewCourseCategory("HS Social Studies");
      await loadAdminData();
    } catch (err) {
      console.error("Error creating course:", err);
      setError("Failed to create course.");
    }
  };

  const copyCategorySnapshot = async () => {
    const lines = [];
    lines.push("Course Category Snapshot");
    lines.push("");
    CATEGORY_OPTIONS.forEach((category) => {
      lines.push(`${category}:`);
      const rows = courses.filter((course) => course.course_category === category);
      if (rows.length === 0) {
        lines.push("  - none");
      } else {
        rows.forEach((course) => {
          lines.push(`  - ${course.name} (Grade ${course.grade_level})`);
        });
      }
      lines.push("");
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
    } catch (err) {
      console.error("Failed to copy category snapshot:", err);
    }
  };

  // Load schools when principal promotion section opens
  useEffect(() => {
    if (!isPrincipalSectionOpen) return;
    schoolAPI.getAll().then((res) => setSchools(res.data || [])).catch(console.error);
  }, [isPrincipalSectionOpen]);

  const togglePrincipalSchool = (id) => {
    setPrincipalSchoolIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handlePromotePrincipal = async () => {
    if (!principalEmail.trim()) {
      setPrincipalPromoteMsg({ type: "error", text: "Email is required." });
      return;
    }
    if (principalSchoolIds.size === 0) {
      setPrincipalPromoteMsg({ type: "error", text: "Select at least one school." });
      return;
    }
    setPromotingPrincipal(true);
    setPrincipalPromoteMsg({ type: "", text: "" });
    try {
      const res = await walkthroughAPI.promotePrincipal({
        email: principalEmail.trim(),
        schoolIds: [...principalSchoolIds],
      });
      setPrincipalPromoteMsg({ type: "success", text: res.data?.message || "Promoted successfully." });
      setPrincipalEmail("");
      setPrincipalSchoolIds(new Set());
    } catch (err) {
      setPrincipalPromoteMsg({
        type: "error",
        text: err.response?.data?.error || "Promotion failed.",
      });
    } finally {
      setPromotingPrincipal(false);
    }
  };

  const handleRefresh = async () => {
    if (
      dirtyCount > 0
      && !window.confirm(
        `You have ${dirtyCount} unsaved course edit(s). Refresh and discard them?`,
      )
    ) {
      return;
    }
    await loadAdminData();
  };

  const handleCalendarWindowSync = async () => {
    if (!syncCourseId) {
      setSyncError("Select a course before syncing calendar windows.");
      return;
    }

    setPreviewingCalendarWindows(true);
    setSyncStatus("");
    setSyncError("");
    try {
      const response = await adminAPI.syncCalendarWindows({
        courseId: syncCourseId,
        schoolYear: syncSchoolYear.trim() || "2025-2026",
        dryRun: true,
      });
      setSyncPreviewData(response.data || null);
      setIsSyncPreviewOpen(true);
    } catch (syncErr) {
      console.error("Error syncing calendar windows:", syncErr);
      setSyncError(
        syncErr?.response?.data?.error
          || "Failed to sync calendar windows.",
      );
    } finally {
      setPreviewingCalendarWindows(false);
    }
  };

  const closeSyncPreview = () => {
    setIsSyncPreviewOpen(false);
    setSyncPreviewData(null);
  };

  const confirmCalendarWindowSync = async () => {
    if (!syncCourseId) return;
    setSyncingCalendarWindows(true);
    setSyncStatus("");
    setSyncError("");
    try {
      const response = await adminAPI.syncCalendarWindows({
        courseId: syncCourseId,
        schoolYear: syncSchoolYear.trim() || "2025-2026",
        dryRun: false,
      });
      const payload = response.data || {};
      setSyncStatus(
        `Synced ${payload.unitsUpdatedCount || 0} unit window(s) and ${payload.unitGuidesUpdatedCount || 0} unit guide window(s).`,
      );
      closeSyncPreview();
    } catch (syncErr) {
      console.error("Error applying calendar window sync:", syncErr);
      setSyncError(
        syncErr?.response?.data?.error
          || "Failed to apply calendar window sync.",
      );
    } finally {
      setSyncingCalendarWindows(false);
    }
  };

  return (
    <div className="admin-page">
      <section className="panel-card admin-overview" data-tour="admin-overview">
        <div className="admin-header-row">
          <div>
            <h2>Admin Workspace</h2>
            <p>
              Curriculum designer tools for scope & sequence, unit guides, and
              course category management.
            </p>
          </div>
          <div className="admin-actions">
            <button
              type="button"
              className={`mode-toggle-btn ${isCurriculumOnlyMode ? "active" : ""}`}
              onClick={() => setIsCurriculumOnlyMode((prev) => !prev)}
            >
              {isCurriculumOnlyMode
                ? "Curriculum-only Mode: On"
                : "Curriculum-only Mode: Off"}
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={handleRefresh}
              data-tour="admin-refresh"
            >
              Refresh
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => setIsAdminPromotionOpen(true)}
            >
              Promote Admins
            </button>
            <button
              type="button"
              className="primary-btn"
              onClick={() => setIsPrincipalSectionOpen((o) => !o)}
            >
              {isPrincipalSectionOpen ? "Hide Principal Promotion" : "Promote Principals"}
            </button>
            <button type="button" className="primary-btn" onClick={copyCategorySnapshot}>
              Copy Category Snapshot
            </button>
          </div>
        </div>

        <div className="curriculum-tools-grid">
          <article className="curriculum-tool-card">
            <h3>Scope & Sequence Editor</h3>
            <p>
              Update unit windows, big ideas, and TEKS pacing entries by course
              and school year.
            </p>
            <button
              type="button"
              className="primary-btn"
              onClick={() => setIsScopeEditorOpen(true)}
            >
              Edit Scope & Sequence
            </button>
          </article>
          <article className="curriculum-tool-card">
            <h3>Unit Guide Editor</h3>
            <p>
              Edit guide metadata and section content used by the Guide page and
              source transcript links.
            </p>
            <button
              type="button"
              className="primary-btn"
              onClick={() => setIsUnitGuideEditorOpen(true)}
            >
              Edit Unit Guides
            </button>
          </article>
          <article className="curriculum-tool-card">
            <h3>Calendar Window Sync</h3>
            <p>
              Recalculate unit and guide date windows from actual calendar lesson
              days for a course and school year.
            </p>
            <div className="calendar-sync-form">
              <label htmlFor="calendar-sync-course">
                Course
                <select
                  id="calendar-sync-course"
                  value={syncCourseId}
                  onChange={(event) => setSyncCourseId(event.target.value)}
                >
                  {courses.map((course) => (
                    <option key={course.id} value={course.id}>
                      {course.name}
                    </option>
                  ))}
                </select>
              </label>
              <label htmlFor="calendar-sync-school-year">
                School Year
                <input
                  id="calendar-sync-school-year"
                  type="text"
                  value={syncSchoolYear}
                  onChange={(event) => setSyncSchoolYear(event.target.value)}
                  placeholder="2025-2026"
                />
              </label>
            </div>
            <button
              type="button"
              className="primary-btn"
              disabled={previewingCalendarWindows || syncingCalendarWindows || !syncCourseId}
              onClick={handleCalendarWindowSync}
            >
              {previewingCalendarWindows ? "Checking..." : "Sync Windows from Calendar"}
            </button>
            {syncStatus && <p className="calendar-sync-success">{syncStatus}</p>}
            {syncError && <p className="calendar-sync-error">{syncError}</p>}
          </article>
        </div>

        {loading && <p>Loading admin data...</p>}
        {error && <p className="admin-error">{error}</p>}

        {!isCurriculumOnlyMode && !loading && overview && (
          <>
            <div className="admin-kpis">
              <div className="admin-kpi">
                <span>Courses</span>
                <strong>{overview.totals.courses}</strong>
              </div>
              <div className="admin-kpi">
                <span>Units</span>
                <strong>{overview.totals.units}</strong>
              </div>
              <div className="admin-kpi">
                <span>Lessons</span>
                <strong>{overview.totals.lessons}</strong>
              </div>
              <div className="admin-kpi">
                <span>TEKS</span>
                <strong>{overview.totals.teks}</strong>
              </div>
              <div className="admin-kpi">
                <span>Calendar Rows</span>
                <strong>{overview.totals.calendarRows}</strong>
              </div>
              <div className="admin-kpi">
                <span>Assets</span>
                <strong>{overview.totals.assets}</strong>
              </div>
            </div>

            <div className="category-summary" data-tour="admin-category-summary">
              <h3>Category Distribution</h3>
              <div className="category-summary-grid">
                <div>MS Math: {overview.categories.msMath || 0}</div>
                <div>MS RLA: {overview.categories.msRla || 0}</div>
                <div>MS Science: {overview.categories.msScience || 0}</div>
                <div>MS Social Studies: {overview.categories.msSocialStudies || 0}</div>
                <div>HS Math: {overview.categories.hsMath}</div>
                <div>HS RLA: {overview.categories.hsRla}</div>
                <div>HS Science: {overview.categories.hsScience}</div>
                <div>HS Social Studies: {overview.categories.hsSocialStudies}</div>
              </div>
              <p className="muted-note">
                Last data update: {formatDateTime(overview.lastUpdated)}
              </p>
            </div>
          </>
        )}
      </section>

      {!isCurriculumOnlyMode && (
        <section className="panel-card admin-create-course" data-tour="admin-create-course">
          <h3>Quick Add Course</h3>
          <form className="course-create-form" onSubmit={createCourse}>
            <input
              type="text"
              placeholder="Course name"
              value={newCourseName}
              onChange={(e) => setNewCourseName(e.target.value)}
            />
            <input
              type="number"
              min="1"
              max="12"
              value={newCourseGrade}
              onChange={(e) => setNewCourseGrade(e.target.value)}
            />
            <select
              value={newCourseCategory}
              onChange={(e) => setNewCourseCategory(e.target.value)}
            >
              {CATEGORY_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <button type="submit" className="primary-btn">
              Add Course
            </button>
          </form>
        </section>
      )}

      <section className="panel-card admin-category-manager" data-tour="admin-category-manager">
        <div className="category-manager-header">
          <h3>Course Category Manager</h3>
          <div className="category-manager-actions">
            <button
              type="button"
              className="primary-btn"
              disabled={dirtyCount === 0}
              onClick={saveAllChanges}
              data-tour="admin-save-all"
            >
              Save All ({dirtyCount})
            </button>
            <button
              type="button"
              className="primary-btn"
              disabled={dirtyCount === 0}
              onClick={resetAllDrafts}
            >
              Reset All Drafts
            </button>
          </div>
        </div>

        <div className="manager-filters" data-tour="admin-filters">
          <input
            type="text"
            placeholder="Search by course name or grade..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="ALL">All Categories</option>
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <select
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
          >
            <option value="category-name">Sort: Category + Name</option>
            <option value="name-asc">Sort: Name (A-Z)</option>
            <option value="name-desc">Sort: Name (Z-A)</option>
            <option value="grade-asc">Sort: Grade (Low-High)</option>
            <option value="grade-desc">Sort: Grade (High-Low)</option>
          </select>
        </div>

        <div className="category-quick-filters">
          <button
            type="button"
            className={`quick-filter-btn ${categoryFilter === "ALL" ? "active" : ""}`}
            onClick={() => setCategoryFilter("ALL")}
          >
            All
          </button>
          {CATEGORY_OPTIONS.map((option) => (
            <button
              key={option}
              type="button"
              className={`quick-filter-btn ${categoryFilter === option ? "active" : ""}`}
              onClick={() => setCategoryFilter(option)}
            >
              {option}
            </button>
          ))}
        </div>

        <div className="manager-summary-row">
          <span>
            Showing {visibleCourses.length} of {courses.length} courses
          </span>
          <span>Unsaved edits: {dirtyCount}</span>
          <span>Selected: {selectedIds.length}</span>
        </div>

        <div className="bulk-tools" data-tour="admin-bulk-tools">
          <button type="button" className="primary-btn" onClick={selectVisibleRows}>
            Select Visible
          </button>
          <button type="button" className="primary-btn" onClick={clearSelection}>
            Clear Selection
          </button>
          <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
            {CATEGORY_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="primary-btn"
            onClick={applyBulkCategory}
            disabled={selectedIds.length === 0}
          >
            Apply Category to Selected
          </button>
        </div>

        <div className="course-table-wrap">
          <table className="course-table">
            <thead>
              <tr>
                <th>Select</th>
                <th>Course</th>
                <th>Grade</th>
                <th>Category</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleCourses.map((course) => {
                const draft = drafts[course.id];
                const saving = savingIds[course.id];
                const view = draft || course;
                return (
                  <tr key={course.id}>
                    <td>
                      <input
                        type="checkbox"
                        checked={Boolean(selectedRows[course.id])}
                        onChange={(e) =>
                          setSelectedRows((prev) => ({
                            ...prev,
                            [course.id]: e.target.checked,
                          }))
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={view.name || ""}
                        onChange={(e) => upsertDraft(course, { name: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        min="1"
                        max="12"
                        value={view.grade_level || ""}
                        onChange={(e) =>
                          upsertDraft(course, { grade_level: Number(e.target.value) })
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={view.course_category || defaultCategoryForCourse(view)}
                        onChange={(e) =>
                          upsertDraft(course, { course_category: e.target.value })
                        }
                      >
                        {CATEGORY_OPTIONS.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {draft ? <span className="dirty-pill">Unsaved</span> : <span>Saved</span>}
                    </td>
                    <td>
                      <div className="row-actions">
                        <button
                          type="button"
                          className="primary-btn"
                          disabled={!draft || saving}
                          onClick={() => saveCourse(course.id)}
                        >
                          {saving ? "Saving..." : "Save"}
                        </button>
                        <button
                          type="button"
                          className="primary-btn"
                          disabled={!draft || saving}
                          onClick={() => resetCourseDraft(course.id)}
                        >
                          Revert
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleCourses.length === 0 && (
                <tr>
                  <td colSpan="6">No courses match the current filters.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ScopeSequenceEditorModal
        isOpen={isScopeEditorOpen}
        onClose={() => setIsScopeEditorOpen(false)}
        courses={courses}
      />
      <UnitGuideEditorModal
        isOpen={isUnitGuideEditorOpen}
        onClose={() => setIsUnitGuideEditorOpen(false)}
        courses={courses}
      />
      {isSyncPreviewOpen && syncPreviewData && (
        <div className="calendar-sync-preview-modal" onClick={closeSyncPreview}>
          <div
            className="calendar-sync-preview-panel"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="calendar-sync-preview-header">
              <div>
                <h3>Review Calendar Window Sync</h3>
                <p>
                  {syncPreviewData.courseName} ({syncPreviewData.schoolYear})
                </p>
              </div>
              <button
                type="button"
                className="calendar-sync-preview-close-btn"
                onClick={closeSyncPreview}
              >
                Cancel
              </button>
            </div>

            <div className="calendar-sync-preview-body">
              <p>
                This will update{" "}
                <strong>{syncPreviewData.unitsUpdatedCount || 0}</strong> unit
                window(s) and{" "}
                <strong>{syncPreviewData.unitGuidesUpdatedCount || 0}</strong>{" "}
                unit guide row(s).
              </p>
              {(syncPreviewData.unitsWithoutLessonDays || []).length > 0 && (
                <p className="calendar-sync-preview-warning">
                  Units without lesson days in this window:{" "}
                  {(syncPreviewData.unitsWithoutLessonDays || []).join(", ")}
                </p>
              )}

              <div className="calendar-sync-preview-grid">
                <article>
                  <h4>Unit Window Changes</h4>
                  {(syncPreviewData.unitChanges || []).length === 0 ? (
                    <p>No unit date changes.</p>
                  ) : (
                    <ul>
                      {(syncPreviewData.unitChanges || []).map((change) => (
                        <li key={change.unitId}>
                          Unit {change.unitNumber}:{" "}
                          {formatDate(change.fromStartDate)} -{" "}
                          {formatDate(change.fromEndDate)} →{" "}
                          {formatDate(change.toStartDate)} -{" "}
                          {formatDate(change.toEndDate)}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>

                <article>
                  <h4>Unit Guide Changes</h4>
                  {(syncPreviewData.unitGuideChanges || []).length === 0 ? (
                    <p>No unit guide date changes.</p>
                  ) : (
                    <ul>
                      {(syncPreviewData.unitGuideChanges || []).map((change) => (
                        <li key={change.guideId}>
                          Unit {change.unitNumber}:{" "}
                          {formatDate(change.fromStartDate)} -{" "}
                          {formatDate(change.fromEndDate)} →{" "}
                          {formatDate(change.toStartDate)} -{" "}
                          {formatDate(change.toEndDate)}
                        </li>
                      ))}
                    </ul>
                  )}
                </article>
              </div>
            </div>

            <div className="calendar-sync-preview-actions">
              <button
                type="button"
                className="primary-btn"
                onClick={closeSyncPreview}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-btn"
                disabled={
                  syncingCalendarWindows
                  || (
                    Number(syncPreviewData.unitsUpdatedCount || 0) === 0
                    && Number(syncPreviewData.unitGuidesUpdatedCount || 0) === 0
                  )
                }
                onClick={confirmCalendarWindowSync}
              >
                {syncingCalendarWindows ? "Applying..." : "Confirm Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
      {isPrincipalSectionOpen && (
        <div className="settings-section panel-card" style={{ marginTop: 16 }}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: "#1d2b3a" }}>
            Promote Principal
          </h3>
          <p style={{ margin: "0 0 14px", fontSize: 13, color: "#647490" }}>
            Assign a teacher the <strong>principal</strong> role and link them to one or more
            schools. They will gain read-only access to the Walkthrough page for each
            assigned building. Principals doing double-duty can be assigned multiple schools.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, maxWidth: 480 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#3a4d65", display: "block", marginBottom: 4 }}>
                Teacher Email
              </label>
              <input
                type="email"
                className="settings-text-input"
                value={principalEmail}
                onChange={(e) => setPrincipalEmail(e.target.value)}
                placeholder="teacher@episd.org"
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: "#3a4d65", display: "block", marginBottom: 4 }}>
                Assign Schools{principalSchoolIds.size > 0 && ` (${principalSchoolIds.size} selected)`}
              </label>
              {schools.length === 0 ? (
                <p style={{ margin: "0", fontSize: 12, color: "#e53e3e" }}>
                  No schools found. Run the walkthrough migration and seed schools first.
                </p>
              ) : (
                <div style={{
                  border: "1px solid #d1d5db",
                  borderRadius: 8,
                  maxHeight: 220,
                  overflowY: "auto",
                  padding: "6px 0",
                }}>
                  {schools.map((s) => (
                    <label
                      key={s.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "5px 12px",
                        cursor: "pointer",
                        fontSize: 13,
                        color: "#1d2b3a",
                        background: principalSchoolIds.has(s.id) ? "#eff6ff" : "transparent",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={principalSchoolIds.has(s.id)}
                        onChange={() => togglePrincipalSchool(s.id)}
                        style={{ accentColor: "#2563eb" }}
                      />
                      <span>{s.name}</span>
                      <span style={{ marginLeft: "auto", fontSize: 11, color: "#647490", textTransform: "capitalize" }}>
                        {s.school_type}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            {principalPromoteMsg.text && (
              <p style={{
                margin: 0,
                fontSize: 13,
                fontWeight: 600,
                color: principalPromoteMsg.type === "success" ? "#15803d" : "#e53e3e",
              }}>
                {principalPromoteMsg.text}
              </p>
            )}
            <button
              type="button"
              className="primary-btn"
              onClick={handlePromotePrincipal}
              disabled={promotingPrincipal}
              style={{ alignSelf: "flex-start" }}
            >
              {promotingPrincipal ? "Promoting…" : "Promote to Principal"}
            </button>
          </div>
        </div>
      )}
      <AdminPromotionModal
        isOpen={isAdminPromotionOpen}
        onClose={() => setIsAdminPromotionOpen(false)}
      />
    </div>
  );
};

export default SettingsPage;
